<template>
  <div :class="fullView ? 'full-view' : 'variations-per-stage-echarts'">
    <v-chart class="chart" :option="option" ref="chartRef" :init-options="{ renderer: 'svg' }"/>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { PieChart } from 'echarts/charts'
import { SVGRenderer } from 'echarts/renderers'
import { TooltipComponent, LegendComponent } from 'echarts/components'
import { use } from 'echarts/core'
import { useChartResize } from '@/composables/useChartResize'
import { COLOR_PALETTE, getDefaultChartTextStyle, getPieNoDataOptions } from '@/helpers'
import VChart from 'vue-echarts'
import { emitChartFinished } from '@/utils/insightsPdfUtils'

use([TooltipComponent, LegendComponent, PieChart, SVGRenderer])

export default {
  name: 'VariationsPerStageChart',
  components: {
    VChart
  },
  props: {
    resultSet: {
      type: Object,
      required: true,
      default: () => ({})
    },
    fullView: {
      type: Boolean
    }
  },
  setup () {
    const { chartRef, handleResize } = useChartResize()

    return {
      chartRef,
      handleResize
    }
  },
  data () {
    return {
      option: {
        animation: !this.fullView,
        title: {
          text: this.$t('insights.charts.variationsPerStage.title'),
          left: 'center',
          top: 0,
          textStyle: getDefaultChartTextStyle(this.fullView)
        },
        tooltip: {
          trigger: 'item'
        },
        legend: {
          left: '5%',
          top: 'center',
          orient: 'vertical',
          type: 'scroll',
          textStyle: getDefaultChartTextStyle(this.fullView),
          width: '25%'
        },
        series: [
          {
            type: 'pie',
            radius: ['40%', '70%'],
            center: ['70%', this.fullView ? '50%' : '60%'],
            label: {
              position: 'inner',
              ...getDefaultChartTextStyle(this.fullView),
              formatter: function (params) {
                return params.value
              }
            },
            data: []
          }
        ]
      },
      statusColors: {
        Request: COLOR_PALETTE[0],
        Proposal: COLOR_PALETTE[1],
        Order: COLOR_PALETTE[2]
      }
    }
  },
  mounted () {
    this.updateData()
  },
  methods: {
    getStatusName (statusIndex) {
      if (!this.variationsStages) {
        return `Status ${statusIndex}`
      }

      const index = String(statusIndex)

      if (index === '0' && this.variationsStages.variation && this.variationsStages.variation[0]) {
        return this.variationsStages.variation[0].name
      }
      if (index === '1' && this.variationsStages.variation && this.variationsStages.variation[1]) {
        return this.variationsStages.variation[1].name
      }
      if (index === '2' && this.variationsStages.variation && this.variationsStages.variation[2]) {
        return this.variationsStages.variation[2].name
      }

      return `Status ${index}`
    },

    updateData () {
      const noDataOptions = getPieNoDataOptions(this.resultSet, this.fullView, this.option)
      if (noDataOptions) {
        this.option = {
          ...this.option,
          ...noDataOptions,
          title: this.option.title
        }
        emitChartFinished(this, true)
        return
      }

      const pivot = this.resultSet.chartPivot()

      const statusGroups = {}

      pivot.forEach(r => {
        const statusIndex = r.x
        const statusName = this.getStatusName(statusIndex)
        const count = r['variations.count'] || 0

        if (!statusGroups[statusName]) {
          statusGroups[statusName] = 0
        }
        statusGroups[statusName] += count
      })

      const seriesData = Object.keys(statusGroups).map(statusName => {
        const value = statusGroups[statusName]
        const color = this.statusColors[statusName] || null

        return {
          value,
          name: statusName,
          itemStyle: color ? { color } : undefined
        }
      })

      seriesData.sort((a, b) => b.value - a.value)

      this.option.series[0].data = seriesData

      emitChartFinished(this, false)
    }
  },
  computed: {
    ...mapState({
      variationsStages: (state) => state.variationsStages
    })
  },
  watch: {
    resultSet: {
      handler () {
        this.updateData()
      },
      deep: true
    },
    variationsStages: {
      handler () {
        this.updateData()
      },
      deep: true
    }
  }
}
</script>

<style scoped>
.variations-per-stage-echarts {
  height: 250px;
}
.full-view {
  height: var(--full-view-chart-height);
  width: var(--full-view-chart-width);
}
</style>
